import React, {CSSProperties, useCallback, useLayoutEffect, useMemo, useState} from "react"; // requires a loader
import {AppWrapper, Column} from "layouts";
import Button from "components/Button";
import styles from './splash_screen.module.css';
import base from '../../assets/images/slashscreen/base.png'
import contractor from '../../assets/images/slashscreen/contractor.png'
import supplier from '../../assets/images/slashscreen/supplier.png'
import {Link} from "react-router-dom";
import {Carousel} from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {BrowserView, isMobile, MobileView} from "react-device-detect";
import {COLORS, SUPPORTED_LANG} from "utils/constants";
import {Helmet} from "react-helmet";
import {useLocalStorage} from "react-use";
import {attemptLogin} from "services/rest_service/authApi";
import {getUserInfo} from "services/rest_service/userApi";
import {setUserId, setUserInfo} from "redux/action/user_actions";
import {useNavigate} from "react-router";
import {useDispatch} from "react-redux";
import {isEmpty} from "lodash";
import {MIXPANEL} from "services/analytics/mixpanel";
import useTranslate from "../../hocs/useTranslate";
import Menu from "components/common/Menu";
import {isApp} from "services/local_service";


const indicatorStyles: CSSProperties = {
  background: '#575758',
  width: 16,
  height: 8,
  display: 'inline-block',
  margin: '8px',
  borderRadius:'20px',
  cursor:'pointer'
};


const SplashScreen = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {TranslateText, changeLanguage, activeLanguage} = useTranslate()

  const [deviceLogin,] = useLocalStorage<any>("device:login", null)
  const [, setSessionId] = useLocalStorage('buildhub:sessionId', null);
  const [loading, setLoading] = useState<boolean>(false)

  const fetchUserData = useCallback(async (token: string) => {
    const {data: {data}} = await getUserInfo(String(token));
    if(data?.id){
      dispatch(setUserId(data.id))
      dispatch(setUserInfo(data))
    }
  }, [])

  const canLogin = useMemo(() =>
    !isEmpty(deviceLogin) ||
    !isEmpty(deviceLogin?.email) ||
    !isEmpty(deviceLogin?.password) ||
    !isEmpty(deviceLogin?.type), [deviceLogin])


  const login = useCallback(async (loginData: any) => {
    // do checks
    setLoading(()=>true)
    try {
      const {data: {data}} = await attemptLogin(loginData);
      const {sessionId, token, refreshToken} = data
      setSessionId(sessionId)
      localStorage.setItem("buildhub:token", token)
      localStorage.setItem("buildhub:refreshToken", refreshToken)
      await fetchUserData(token);
      navigate(`/${String(deviceLogin?.type).toLowerCase()}/dashboard`);

    } catch (e) {
      navigate("/auth/login/as")
    } finally {
      setLoading(false)
    }

  }, []);

  const createAccountAction = useCallback(()=> {
      MIXPANEL.track("New Account")
      navigate("/auth/signup/as")
  }, [MIXPANEL, navigate]);

  const loginAction = useCallback(()=> {
    MIXPANEL.track("Login Page")
    navigate("/auth/login/as")
  }, []);

  useLayoutEffect(() => {
    if(isApp() && canLogin){
      login(deviceLogin).then().catch()
    }
  }, [isApp, login]);


  return (
    <AppWrapper showMobile hidePad
                renderRight={
      <Menu
        menu={SUPPORTED_LANG?.map((lang)=> {
          return {
            label: `${lang.name} ${!lang.active ? " - coming soon" : ""}`,
            key: lang.key,
            onClick: async () => {
              if(lang.active){
                await changeLanguage(lang.key)
              }
            }
          }
        })}
        trigger={<div style={{
          textTransform:'uppercase',
          padding: isMobile ? '0.5rem' : '1rem',
          marginRight:'-0.5rem'
        }} className={"flat-button"}>{activeLanguage}</div>}/>
    }>
      <Helmet>
        <title>BuildHub App</title>
      </Helmet>
      <Column style={{flex: 1, boxSizing: 'border-box', backgroundColor: isMobile ? COLORS.colorWhite : 'transparent', justifyContent:'center'}}>
        <BrowserView>
          <div className={styles.page_container}>
            <div className={styles.container_details}>
              <div className={styles.center_in_parent}>

                <Column className={styles.text_container}>
                  <h1>
                    <TranslateText i18nKey={"splashScreen.welcomeToBuildHub"}>
                      Welcome to BuildHub
                    </TranslateText>
                  </h1>
                </Column>

                <div className={styles.btn_container}>
                  <Button
                    style={{minWidth:'200px'}}
                    onClick={createAccountAction} disabled={loading} primary fullWidth>
                    <TranslateText i18nKey={"auth.createAccount"}>Create an Account</TranslateText>
                  </Button>
                  <div>
                    {
                      canLogin ?
                        <Button
                          style={{minWidth: '200px'}}
                          disabled={loading}
                          loading={loading}
                          onClick={() => {
                            login(deviceLogin).catch()
                          }} className={styles.login_btn}>
                          <TranslateText i18nKey={"auth.goToDashboard"}>Go to Dashboard</TranslateText>
                        </Button> :
                        <Button
                          style={{minWidth: '200px'}}
                          disabled={loading}
                          onClick={loginAction} className={styles.login_btn}>
                          <TranslateText i18nKey={"sign-in"}>Sign In</TranslateText>
                        </Button>
                    }
                  </div>
               </div>
             </div>
           </div>

           <div className={styles.container_image}>
             <Carousel
               interval={5000}
               infiniteLoop={true}
               showStatus={false}
               swipeable={true}
               autoPlay={true}
               showThumbs={false}
               showArrows={false}
               renderIndicator={(onClickHandler, isSelected, index, label) => {
                 if (isSelected) {
                   return (
                     <li
                       style={{ ...indicatorStyles }}
                     />
                   );
                 }
                 return (
                   <li
                     style={{ ...indicatorStyles, width: '8px', background:'#D9D9D9' }}
                     onClick={onClickHandler}
                     onKeyDown={onClickHandler}
                     value={index}
                     key={index}
                     role="button"
                     tabIndex={0}
                   />
                 );
               }}
             >
               <Column className={styles.carousel_item}>
                 <img alt={'right-image'} src={base} loading={"lazy"}/>
                 <p><TranslateText i18nKey={"splashScreen.tagLine"}>
                   The leading construction marketplace for contractors and suppliers to order materials, transact
                   efficiently, save money, and improve operations.
                 </TranslateText></p>
               </Column>
               <Column className={styles.carousel_item}>
                 <img alt={'right-image'} src={contractor} loading={"lazy"}/>
                 <h4><TranslateText i18nKey={"splashScreen.asContractor"}>
                   As a contractor you can
                 </TranslateText></h4>
                 <p>
                   <TranslateText i18nKey={"splashScreen.contractorTagLine"}>
                     buy your materials from local suppliers, track orders and deliveries in real real time,
                     and pay online, all in one place!
                   </TranslateText>
                 </p>
               </Column>
               <Column className={styles.carousel_item}>
                 <img alt={'right-image'} src={supplier} loading={"lazy"}/>
                 <h4>
                   <TranslateText i18nKey={"splashScreen.asSupplier"}>
                     As a supplier you will
                   </TranslateText>
                 </h4>
                 <p><TranslateText i18nKey={"splashScreen.supplierTagLine"}>
                   streamline orders, payments and operations efficiently with automatic updates, and drive more
                   revenue!
                 </TranslateText></p>
               </Column>
             </Carousel>

           </div>
          </div>
        </BrowserView>
        <MobileView>
          <Column
            style={{
              width: '100vw',
              height: '100dvh',
              padding: '1.4rem',
              flex: 1,
              justifyContent: 'center',
              alignItems: 'space-between',
              background: 'white',
              gap: '2rem'
            }}
          >
            <Column>
              <Carousel
                showStatus={false}
                interval={5000}
                autoPlay={false}
                showThumbs={false}
                showArrows={false}
                infiniteLoop={false}
                swipeable={true}
                renderIndicator={(onClickHandler, isSelected, index, label) => {
                  if (isSelected) {
                    return (
                      <li
                        style={{...indicatorStyles}}
                        aria-label={`Selected: ${label} ${index + 1}`}
                        title={`Selected: ${label} ${index + 1}`}
                      />
                    );
                  }
                  return (
                    <li
                      style={{...indicatorStyles, width: '8px', background: '#D9D9D9'}}
                      onClick={onClickHandler}
                      onKeyDown={onClickHandler}
                      value={index}
                      key={index}
                      role="button"
                      tabIndex={0}
                      title={`${label} ${index + 1}`}
                      aria-label={`${label} ${index + 1}`}
                    />
                  );
                }}
              >
                <Column className={styles.carousel_item}>
                  <img alt={'right-image'} src={base} loading={"lazy"}/>
                  <h3 style={{marginTop: '1rem', padding: '0', fontSize:'18px'}}>
                    <TranslateText i18nKey={"splashScreen.welcomeToBuildHub"}>
                      Welcome to BuildHub
                    </TranslateText></h3>
                  <p style={{maxWidth:'400px'}}><TranslateText i18nKey={"splashScreen.tagLine"}>
                    The leading construction marketplace for contractors and suppliers to order materials, transact
                    efficiently, save money, and improve operations.
                  </TranslateText></p>
                </Column>
                <Column className={styles.carousel_item}>
                  <img alt={'right-image'} src={contractor} loading={"lazy"}/>
                  <h3  style={{marginTop: '1rem', padding: '0', fontSize:'18px'}}><TranslateText i18nKey={"splashScreen.asContractor"}>
                    As a contractor you can
                  </TranslateText></h3>
                  <p>
                    <TranslateText i18nKey={"splashScreen.contractorTagLine"}>
                      buy your materials from local suppliers, track orders and deliveries in real real time,
                      and pay online, all in one place!
                    </TranslateText>
                  </p>
                </Column>
                <Column className={styles.carousel_item}>
                  <img alt={'right-image'} src={supplier} loading={"lazy"}/>
                  <h3  style={{marginTop: '1rem', padding: '0', fontSize:'18px'}}>
                    <TranslateText i18nKey={"splashScreen.asSupplier"}>
                      As a supplier you will
                    </TranslateText>
                  </h3>
                  <p><TranslateText i18nKey={"splashScreen.supplierTagLine"}>
                    streamline orders, payments and operations efficiently with automatic updates, and drive more
                    revenue!
                  </TranslateText></p>
                </Column>
              </Carousel>
            </Column>
            <Column className={styles.btn_container}>
              <Link to={"/auth/signup/as"}>
                <Button
                  onClick={createAccountAction}
                  style={{minWidth:'280px'}}
                  disabled={loading}
                  primary>
                  <TranslateText i18nKey={"auth.createAccount"}>Create an Account</TranslateText>
                </Button>
              </Link>
              <div>
                {
                  canLogin ? <Button
                      style={{minWidth:'280px'}}
                      loading={loading}
                      disabled={loading}
                      onClick={() => {
                        login(deviceLogin).catch()
                      }}>
                      <TranslateText i18nKey={"auth.goToDashboard"}>Go to Dashboard</TranslateText>
                    </Button> :

                    <Button
                      style={{minWidth:'280px'}}
                      disabled={loading}
                      onClick={loginAction} className={styles.login_btn}>
                      <TranslateText i18nKey={"auth.signIn"}>Sign In</TranslateText>
                    </Button>
                }
              </div>
            </Column>
          </Column>
        </MobileView>
      </Column>
    </AppWrapper>
  )
}


// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default SplashScreen
